import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui';
import { isDictationAvailable, startDictation, type DictationHandle } from '@/lib/speech';

/**
 * 음성으로 쓰기 (v1.19) — 텍스트 입력 옆에 붙는 받아쓰기 버튼.
 * 확정된 문장을 onText로 넘긴다. 기기가 지원하지 않으면 아예 보이지 않는다.
 */
export function DictationButton({ onText }: { onText: (text: string) => void }) {
  const [available, setAvailable] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleRef = useRef<DictationHandle | null>(null);

  useEffect(() => {
    void isDictationAvailable().then(setAvailable);
    return () => void handleRef.current?.stop();
  }, []);

  if (!available) return null;

  const toggle = async () => {
    if (listening) {
      await handleRef.current?.stop();
      return;
    }
    setError(null);
    setListening(true);
    handleRef.current = await startDictation(onText, (caught) => {
      setListening(false);
      handleRef.current = null;
      if (caught) setError(caught);
    });
  };

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant={listening ? 'primary' : 'outline'}
        icon={listening ? <Square size={14} /> : <Mic size={14} />}
        onClick={() => void toggle()}
      >
        {listening ? '받아쓰는 중 · 멈추기' : '음성으로 쓰기'}
      </Button>
      {listening && (
        <span className="flex items-center gap-1.5 text-sm text-primary-strong">
          <span className="h-2 w-2 animate-pulse rounded-full bg-primary" aria-hidden />
          말씀하시면 글로 옮겨 적어요
        </span>
      )}
      {error && <span className="text-sm text-ink-sub">{error}</span>}
    </div>
  );
}
